import React, { useCallback, useEffect, useState } from 'react';
import { InfoContainer } from '../style';
import { RECIPIENT_SCREEN, ADD_RECIPIENT } from '../../../utils/constants';
import { change, Field, getFormValues, reduxForm } from 'redux-form';
import { OutlinedTextFields, CommonDropdownOutlined, CommonButton } from '@Components';
import {
  alphanumeric,
  dynamic_validator2,
  minLength2,
  required,
  textWithSpacesOnly,
  validateRoutingValue,
  r_maxLength
} from '@Utils/validation';
import { useNavigate } from 'react-router-dom';
import { activateLoader, disableLoader, scrollToError } from '@Redux/actions/ui-trigger';
import { IStore } from '../../../../../store.type';
import { connect } from 'react-redux';
import { INDIVIDUAL, PAYEE_LIST_URL } from '@Utils/constants';
import { API_URL } from '@Utils/config';
import { Col, FormContainer, Row } from '../../../styles';
import AddressBlock from '../../../../../containers/address-block';
import {
  fetchBeneCurrencies,
  fetchDropdownCountries,
  getAddressConfig
} from '@Redux/actions/individual-user-flow';
import { IAddRecipient } from '../../../utils/interface';
import { handleError, showMessage } from '@Redux/actions/common-message';
import {
  addNiumForexBene,
  logAddRecipientEvent,
  updateNiumForexBene,
  getSecondaryRoutingCode,
  SECONDARY_ROUTING_INITIAL_VALUE,
  getSecondaryRoutingCodePlaceHolder
} from '../utils';
import { updateTempTransactionWithId } from '../../../utils/util';
import { changeNiumForexScreen, storeSelectedBeneId } from '@Redux/actions/nium-forex';
import { NF_SCREENS } from '../../../root/screenConstants';
import { ADD_RECIPIENT_FORM } from './addRecipientForm';
import { initializeForm } from '@Utils/library';

const AddRecipient: React.FC<IAddRecipient> = (props): React.ReactElement => {
  const navigate = useNavigate();
  const RECIPIENT_CONST = RECIPIENT_SCREEN.addRecipient;
  const {
    handleSubmit,
    getAllformVal,
    sourceCurrency,
    destinationCurrency,
    dispatch,
    isRecipientsAddFlow,
    isSendMoneyFlow,
    addNiumForexBene,
    getSecondaryRoutingCode,
    updateNiumForexBene,
    updateTempTransactionWithId,
    tempTransactionId,
    activateLoader,
    disableLoader,
    changeNiumForexScreen,
    storeSelectedBeneId,
    isRecipientEditFlow,
    getAddressConfig,
    beneItem,
    initialize,
    secondaryRoutingFields,
    secondaryRoutingFields: { showSecondaryRoutingCode, isAccountNumberRequired }
  } = props;
  const accTypeArray = [{ key: INDIVIDUAL, value: INDIVIDUAL, text: 'Individual' }];
  const [receivingCountriesStaticValues, setReceivingCountriesStaticValues] = useState([]);
  const [secondaryRoutingCode, setSecondaryRoutingCode] = useState(SECONDARY_ROUTING_INITIAL_VALUE);
  const [destinationCurrencyStaticValues, setDestinationCurrencyStaticValues] = useState(
    isSendMoneyFlow
      ? [
          {
            key: destinationCurrency,
            value: destinationCurrency,
            text: destinationCurrency,
            name: destinationCurrency
          }
        ]
      : []
  );

  useEffect(() => {
    const fetchData = async () => {
      await fetchCountries();
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isRecipientEditFlow) {
      const {
        account_type,
        bank_account_country,
        currency_code,
        remitter_beneficiary_relationship_key,
        full_name,
        routing_code,
        secondary_routing_code,
        bank_name,
        bank_account_number,
        country_code,
        address
      } = beneItem;

      const initialData = {
        account_type,
        bank_account_country_code: bank_account_country,
        currency_code,
        remitter_beneficiary_relationship: remitter_beneficiary_relationship_key,
        full_name,
        routing_code,
        secondary_routing_code,
        bank_name,
        bank_account_number,
        country_code: country_code,
        building_name: address?.building_name,
        building_number: address?.building_number,
        street: address?.street,
        street_name: address?.street_name,
        street_type: address?.street_type,
        unit_number: address?.unit_number,
        floor_number: address?.floor_number,
        address1: address?.address1,
        address2: address?.address2,
        street_number: address?.street_number,
        city: address?.city,
        district: address?.district,
        state: address?.state,
        town: address?.town,
        suburb: address?.suburb,
        province: address?.province,
        block_number: address?.block_number,
        postcode: address?.postcode
      };
      initialize(initializeForm(ADD_RECIPIENT_FORM, initialData));
      setDestinationCurrencyStaticValues([
        {
          key: currency_code,
          value: currency_code,
          text: currency_code,
          name: currency_code
        }
      ]);
      setSecondaryRoutingCode(secondaryRoutingFields);
      dispatch(
        change(
          ADD_RECIPIENT,
          'secondary_routing_code_type',
          secondaryRoutingFields?.secondaryRoutingCodeType
        )
      );
    }
  }, [showSecondaryRoutingCode, isAccountNumberRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCountries = async () => {
    activateLoader();
    const urlData = {
      url: `${API_URL}/beneficiary/countries/dropdown${
        (destinationCurrency &&
          `?destination_currency=${destinationCurrency}&source_currency=${sourceCurrency}&account_type=${INDIVIDUAL}`) ||
        `?account_type=${INDIVIDUAL}`
      }`
    };
    const countriesData = await dispatch(fetchDropdownCountries(urlData));
    disableLoader();
    //@ts-ignore
    setReceivingCountriesStaticValues(countriesData);
    isSendMoneyFlow && dispatch(change(ADD_RECIPIENT, 'currency_code', destinationCurrency));
  };

  const setRoutingFieldSecondary = (
    secondary_routing_config: { code: string; validation: any },
    response: any
  ) => {
    setSecondaryRoutingCode({
      showSecondaryRoutingCode: secondary_routing_config ? true : false,
      secondaryRoutingCodePlaceHolder: secondary_routing_config
        ? getSecondaryRoutingCodePlaceHolder(secondary_routing_config?.code)
        : '',
      secondaryRoutingCodeType: secondary_routing_config?.code,
      validations: secondary_routing_config?.validation,
      isAccountNumberRequired: response?.data?.is_account_number_required
    });
    dispatch(change(ADD_RECIPIENT, 'secondary_routing_code_type', secondary_routing_config?.code));
  };

  const onRecipientCurrencyChange = async (value: any) => {
    activateLoader();
    if (getAllformVal?.bank_account_country_code) {
      const response = await getSecondaryRoutingCode(
        getAllformVal?.bank_account_country_code || '',
        value?.value
      );
      if (response) {
        const { secondary_routing_config } = response?.data || '';
        setRoutingFieldSecondary(secondary_routing_config, response);
      }
    }
    disableLoader();
  };

  const onRecipientCountryChange = async (value: any) => {
    activateLoader();
    const urlData = {
      url: `${API_URL}/beneficiary/currencies/dropdown?country_code=${value?.value}&account_type=${INDIVIDUAL}`
    };
    const currenciesData = await dispatch(fetchBeneCurrencies(urlData));
    // @ts-ignore
    setDestinationCurrencyStaticValues(currenciesData);
    dispatch(change(ADD_RECIPIENT, 'bank_account_country_name', value?.text)); //Needed for TransactionSummary Page
    dispatch(change(ADD_RECIPIENT, 'routing_code', ''));
    dispatch(change(ADD_RECIPIENT, 'secondary_routing_code', ''));
    dispatch(change(ADD_RECIPIENT, 'bank_name', ''));
    //@ts-ignore
    if (getAllformVal?.currency_code || currenciesData?.length === 1) {
      const response = await getSecondaryRoutingCode(
        value?.value,
        //@ts-ignore
        currenciesData[0]?.value || getAllformVal?.currency_code
      );
      if (response) {
        const { secondary_routing_config } = response?.data || '';
        setRoutingFieldSecondary(secondary_routing_config, response);
      }
    }
    disableLoader();
  };

  const onSubmitRecipientDetails = async () => {
    if (getAllformVal?.country_code === 'HK') {
      if (!getAllformVal?.city) {
        dispatch(showMessage({ text: ['Please select address from search'], type: 'info' }));
        return;
      }
    } else if (!getAllformVal?.postcode) {
      dispatch(showMessage({ text: ['Please select address from search'], type: 'info' }));
      return;
    }

    if (isSendMoneyFlow || isRecipientsAddFlow) {
      activateLoader();
      let payload = {
        ...getAllformVal,
        bank_account_number:
          secondaryRoutingCode.secondaryRoutingCodeType === 'IBAN'
            ? getAllformVal?.secondary_routing_code
            : getAllformVal?.bank_account_number,
        routing_code_type: 'SWIFT'
      };
      if (getAllformVal?.secondary_routing_code !== '') {
        payload = {
          ...payload,
          secondary_routing_code_type: secondaryRoutingCode.secondaryRoutingCodeType
        };
      }
      const response = await addNiumForexBene(payload);
      if (response?.data?.beneficiary_id) {
        const eventData = {
          recipientType: getAllformVal?.account_type,
          recipientCountry: getAllformVal?.bank_account_country_code,
          currency: getAllformVal?.currency_code,
          relationship: getAllformVal?.remitter_beneficiary_relationship,
          recipientFullName: getAllformVal?.full_name,
          recipientBankDetails: {
            bankName: getAllformVal?.bank_name,
            bankAccountNumber: getAllformVal?.bank_account_number,
            swiftCode: getAllformVal?.routing_code,
            secondaryRoutingCode: getAllformVal?.secondary_routing_code
          }
        };
        if (isSendMoneyFlow) {
          const beneId = response?.data?.beneficiary_id;
          storeSelectedBeneId(beneId);
          logAddRecipientEvent(eventData, isSendMoneyFlow);
          const tempTransResp = await updateTempTransactionWithId(tempTransactionId, {
            beneficiary_id: beneId
          });
          if (tempTransResp) {
            changeNiumForexScreen(NF_SCREENS.upload_documents);
          }
          return;
        } else {
          //Recipient Addition via Payee List screen
          logAddRecipientEvent(eventData);
          navigate(PAYEE_LIST_URL);
        }
      }
      disableLoader();
    } else {
      try {
        //Recipient Edit flow
        activateLoader();
        //Get only required address fields for payload for selected country
        const addressConfig = await getAddressConfig({
          country_code: getAllformVal?.country_code || ''
        });
        if (addressConfig?.data) {
          const addressFields = Object.keys(addressConfig?.data);
          const addressValues = {};
          for (const key of addressFields) {
            //@ts-ignore
            addressValues[key] = getAllformVal[key];
          }
          let payload = {
            remitter_beneficiary_relationship: getAllformVal?.remitter_beneficiary_relationship,
            full_name: getAllformVal?.full_name,
            routing_code: getAllformVal?.routing_code,
            bank_name: getAllformVal?.bank_name,
            bank_account_number:
              secondaryRoutingCode.secondaryRoutingCodeType === 'IBAN'
                ? getAllformVal?.secondary_routing_code
                : getAllformVal?.bank_account_number,
            country_code: getAllformVal?.country_code,
            ...addressValues
          };

          if (getAllformVal?.secondary_routing_code !== '') {
            payload = {
              ...payload,
              //@ts-ignore
              secondary_routing_code: getAllformVal?.secondary_routing_code,
              secondary_routing_code_type: secondaryRoutingCode.secondaryRoutingCodeType
            };
          }
          //@ts-ignore
          const response = await updateNiumForexBene(beneItem?.beneficiary_id, payload);
          if (response?.beneficiary_id) {
            navigate(PAYEE_LIST_URL);
          } else dispatch(showMessage({ text: ['Something went wrong'], type: 'info' }));
        }
        disableLoader();
      } catch (error) {
        disableLoader();
        handleError(error);
      }
    }
  };

  const validateField = useCallback(
    (value: any, allValues: any, props: any, name: any) => {
      let errors;
      //eslint-disable-next-line
      errors = dynamic_validator2(value, secondaryRoutingCode.validations, errors, {
        allValues,
        name
      });
      return errors;
    },
    [secondaryRoutingCode.validations]
  );

  return (
    <>
      <FormContainer marginTop={48}>
        <form id={ADD_RECIPIENT} onSubmit={handleSubmit(onSubmitRecipientDetails)}>
          <InfoContainer>
            <div className="heading">{RECIPIENT_CONST.recipientInfo}</div>
            <Row padding={[16, 0, 10, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.account_type}
                  name="account_type"
                  type="text"
                  component={CommonDropdownOutlined}
                  placeholder={RECIPIENT_CONST.recipientType}
                  validate={[required]}
                  options={accTypeArray}
                  staticValues={accTypeArray}
                  autoComplete={true}
                  preselect_if_one_item
                  disabled={!isSendMoneyFlow && !isRecipientsAddFlow}
                  width="100%"
                />
              </Col>
            </Row>
            <Row padding={[10, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.bank_account_country_code}
                  name="bank_account_country_code"
                  type="text"
                  component={CommonDropdownOutlined}
                  placeholder={RECIPIENT_CONST.recipientReceivingCountry}
                  validate={[required]}
                  onDropdownChange={(value: any) => onRecipientCountryChange(value)}
                  staticValues={receivingCountriesStaticValues || []}
                  disabled={!isSendMoneyFlow && !isRecipientsAddFlow}
                  width="100%"
                />
              </Col>
            </Row>
            <Row padding={[10, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.currency_code}
                  name="currency_code"
                  type="text"
                  component={CommonDropdownOutlined}
                  placeholder={RECIPIENT_CONST.currency}
                  validate={[required]}
                  staticValues={destinationCurrencyStaticValues}
                  preselect_if_one_item={true}
                  onDropdownChange={(value: any) => onRecipientCurrencyChange(value)}
                  disabled={
                    (isSendMoneyFlow && destinationCurrency) ||
                    (!isSendMoneyFlow && !isRecipientsAddFlow)
                  }
                  width="100%"
                />
              </Col>
            </Row>
            <Row padding={[10, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.remitter_beneficiary_relationship}
                  name="remitter_beneficiary_relationship"
                  type="text"
                  component={CommonDropdownOutlined}
                  width="100%"
                  placeholder={RECIPIENT_CONST.relationWithRecip}
                  validate={[required]}
                  url={`${API_URL}/niumForex/instarem-object/dropdown?type=REMITTER_BENEFICIARY_RELATIONSHIP&account_type=${INDIVIDUAL}`}
                />
              </Col>
            </Row>
            <Row padding={[20, 0, 30, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.full_name}
                  parentClassName="form-text-field"
                  type="text"
                  name="full_name"
                  component={OutlinedTextFields}
                  maxWidth="450px"
                  validate={[required, minLength2, textWithSpacesOnly]}
                  normalize={r_maxLength(50)}
                  disableUnderlineStyling={true}
                  showLabel={true}
                  placeholder={RECIPIENT_CONST.recipiFullName}
                />
              </Col>
            </Row>
          </InfoContainer>
          <InfoContainer>
            <div className="heading">{RECIPIENT_CONST.recipientBankDetails}</div>
            <Row padding={[26, 0, 10, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.routing_code}
                  parentClassName="form-text-field"
                  type="text"
                  name="routing_code"
                  component={OutlinedTextFields}
                  maxWidth="450px"
                  validate={[required, alphanumeric]}
                  normalize={r_maxLength(11)}
                  disableUnderlineStyling={true}
                  showLabel={true}
                  placeholder={RECIPIENT_CONST.swift}
                />
                <div className="swiftCodeContainer">
                  Find SWIFT code{' '}
                  <a
                    href="https://www2.swift.com/bsl/"
                    className="here"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                </div>
              </Col>
            </Row>
            {secondaryRoutingCode.showSecondaryRoutingCode && (
              <Row padding={[12, 0, 18, 0]}>
                <Col width={450}>
                  <Field
                    bindToThisObject={getAllformVal?.secondary_routing_code}
                    parentClassName="form-text-field"
                    type="text"
                    name="secondary_routing_code"
                    component={OutlinedTextFields}
                    maxWidth="450px"
                    validate={validateField}
                    disableUnderlineStyling={true}
                    showLabel={true}
                    placeholder={secondaryRoutingCode.secondaryRoutingCodePlaceHolder}
                  />
                </Col>
              </Row>
            )}
            <Row padding={[10, 0]}>
              <Col width={450}>
                <Field
                  bindToThisObject={getAllformVal?.bank_name}
                  parentClassName="form-text-field"
                  type="text"
                  name="bank_name"
                  component={OutlinedTextFields}
                  maxWidth="450px"
                  validate={[required]}
                  disableUnderlineStyling={true}
                  showLabel={true}
                  placeholder={RECIPIENT_CONST.bankName}
                  disabled
                />
              </Col>
            </Row>
            {secondaryRoutingCode.isAccountNumberRequired && (
              <Row padding={[15, 0, 30, 0]}>
                <Col width={450}>
                  <Field
                    bindToThisObject={getAllformVal?.bank_account_number}
                    parentClassName="form-text-field"
                    type="text"
                    name="bank_account_number"
                    component={OutlinedTextFields}
                    maxWidth="450px"
                    validate={[required, alphanumeric]}
                    normalize={r_maxLength(50)}
                    disableUnderlineStyling={true}
                    showLabel={true}
                    placeholder={RECIPIENT_CONST.bankAccountNumber}
                  />
                </Col>
              </Row>
            )}
          </InfoContainer>
          <InfoContainer>
            <div className="heading">{RECIPIENT_CONST.recipientAddress}</div>
            <AddressBlock
              formName={ADD_RECIPIENT}
              renderCountryOfResidence={true}
              sectionHead={'Recipient address'}
              {...props}
              //@ts-ignore
              countryCode={getAllformVal?.country_code} //here country_code reffered for Country of Residence.
              boxInputSearchNewUi={true}
            />
          </InfoContainer>
          <Row padding={[35, 0, 0, 0]}>
            <Col width={450}>
              <CommonButton
                text={isSendMoneyFlow ? RECIPIENT_CONST.buttonMsg : RECIPIENT_CONST.saveRecipient}
                width={450}
                height={46}
              />
            </Col>
          </Row>
        </form>
      </FormContainer>
    </>
  );
};

const asyncValidate = () => (values: any, dispatch: any, props: any, field: string) => {
  return new Promise((resolve, reject) => {
    const validationArray = [];

    if (field === 'routing_code') {
      if (values?.routing_code?.length < 8) {
        const errors = {};
        //@ts-ignore
        errors['routing_code'] = `Length must not be less than 8`;
        reject(errors);
        return;
      }
      validationArray.push(
        validateRoutingValue(
          {
            routing_code_type_1: 'SWIFT',
            routing_code_value_1: values?.routing_code,
            country_code: values?.bank_account_country_code
          },
          'routing-code/swift'
        )
      );
    } else {
      if (values?.secondary_routing_code_type === 'IBAN') {
        validationArray.push(
          validateRoutingValue(
            { bank_account_number: values?.secondary_routing_code },
            'routing-code/iban',
            'POST'
          )
        );
      } else if (values?.secondary_routing_code_type === 'BSB') {
        validationArray.push(
          validateRoutingValue(
            {
              routing_code_type_1: 'BSB',
              routing_code_value_1: values?.secondary_routing_code,
              country_code: values?.bank_account_country_code
            },
            'routing-code/in-house-db'
          )
        );
      }
    }

    Promise.all(validationArray)
      .then((result) => {
        const errors = {};
        result.forEach((key, index) => {
          if (!result[index]?.data?.success) {
            dispatch(change(ADD_RECIPIENT, 'bank_name', ''));
            if (field === 'routing_code') {
              //@ts-ignore
              errors['routing_code'] = `Invalid SWIFT`;
            } else {
              //@ts-ignore
              errors['secondary_routing_code'] =
                values?.secondary_routing_code_type === 'IBAN' ? `Invalid IBAN` : `Invalid BSB`;
            }
          } else {
            const data = result[index]?.data?.data;
            if (data?.address?.country_code !== values?.bank_account_country_code) {
              dispatch(change(ADD_RECIPIENT, 'bank_name', ''));
              if (field === 'routing_code') {
                //@ts-ignore
                errors['routing_code'] = `Invalid SWIFT`;
              } else {
                //@ts-ignore
                errors['secondary_routing_code'] =
                  values?.secondary_routing_code_type === 'IBAN' ? `Invalid IBAN` : `Invalid BSB`;
              }
            } else {
              dispatch(change(ADD_RECIPIENT, 'beneficiary_bank_address_info', data?.address));
              dispatch(change(ADD_RECIPIENT, 'bank_name', data?.institution_name));
            }
          }
        });

        if (Object.keys(errors).length === 0) {
          resolve(undefined);
        } else {
          reject(errors);
        }
      })
      .catch((err) => {
        dispatch(showMessage({ text: ['Something went wrong'], type: 'info' }));
        reject(err);
      });
  });
};

const createReduxForm = reduxForm({
  form: ADD_RECIPIENT,
  onSubmitFail: scrollToError,
  asyncValidate: asyncValidate(),
  asyncBlurFields: ['routing_code', 'secondary_routing_code']
});

const mapStateToProps = (reduxState: IStore) => {
  const {
    rootReducer: {
      niumForexReducer: {
        transactionDetails: { tempTransactionId }
      }
    }
  } = reduxState;

  const getAllformVal = getFormValues(ADD_RECIPIENT)(reduxState);
  return {
    getAllformVal,
    tempTransactionId
  };
};

export default connect<any, any, any, IStore>(mapStateToProps, {
  activateLoader,
  disableLoader,
  addNiumForexBene,
  getSecondaryRoutingCode,
  updateNiumForexBene,
  updateTempTransactionWithId,
  changeNiumForexScreen,
  storeSelectedBeneId,
  getAddressConfig
  //@ts-ignore
})(createReduxForm(AddRecipient));
